import { Button } from "@opencode/ui/button"
import { useDialog } from "@opencode/ui/context/dialog"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode/ui/dialog"
import { Icon } from "@opencode/ui/icon"
import { Spinner } from "@opencode/ui/spinner"
import { TextInput } from "@opencode/ui/text-input"
import { useLanguage } from "@/runtime/i18n/language"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal } from "@/runtime/server/runtime"
import type { ServerSDK } from "@/runtime/server/client"
import type { CloudConnection } from "@/runtime/server/cloud-context"
import { useRemoteWorkspace } from "@/runtime/server/remote"
import { sameDirectory } from "@/workspaces/paths"
import { createEffect, createResource, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import "./remote-project.css"

function projectName(value: string) {
  const name = value.trim()
  if (!name || name === "." || name === ".." || /[\\/\x00-\x1f\x7f]/.test(name)) return
  return name
}

async function list(sdk: ServerSDK, directory: string, signal: AbortSignal) {
  const result = await sdk.api.file.list({ path: directory, location: { directory } }, { signal })
  return result.data
    .filter((entry) => entry.type === "directory")
    .map((entry) => entry.path.replaceAll("\\", "/").replace(/\/$/, "").split("/").at(-1) ?? "")
    .filter((name) => projectName(name) === name)
}

export function RemoteProjectDialog(props: { onAdded?: (server: ServerConnection.Any, directory: string) => void }) {
  const dialog = useDialog()
  const language = useLanguage()
  const global = useGlobal()
  const remote = useRemoteWorkspace()
  const abort = new AbortController()
  const [connection, { refetch: reconnect }] = createResource<CloudConnection>((_, info) =>
    remote.ensureRemote(abort.signal, !!info.refetching),
  )
  const root = () => connection()?.directory.replace(/\/+$/, "") ?? ""
  const [state, setState] = createStore({
    selected: "",
    creating: false,
    name: "",
    search: "",
    submitting: false,
    error: "",
  })
  let disposed = false
  onCleanup(() => {
    disposed = true
    abort.abort()
  })
  createEffect(() => {
    if (connection.error || connection.loading) return
    const current = connection()
    if (current && (!remote.available() || remote.state.connection?.key !== current.key)) dialog.close()
  })
  const server = () =>
    connection.error
      ? undefined
      : global.servers.list().find((item) => item.type === "remote" && item.id === connection()?.key)
  const ready = () => {
    const item = server()
    return item && global.ensureServerCtx(item).sdk.connection.status() === "connected" ? item : undefined
  }
  const [projects, { refetch }] = createResource(ready, (item) =>
    list(global.ensureServerCtx(item).sdk, connection()!.directory, abort.signal),
  )
  const names = () => (projects.error ? [] : (projects() ?? []))
  const added = (name: string) => {
    const item = server()
    return (
      !!item &&
      global
        .ensureServerCtx(item)
        .projects.list()
        .some((project) => sameDirectory(project.worktree, `${root()}/${name}`))
    )
  }
  const filtered = () => names().filter((name) => name.toLowerCase().includes(state.search.trim().toLowerCase()))
  const nameError = () => {
    if (!state.name) return
    const name = projectName(state.name)
    if (!name) return language.t("project.remote.invalidName")
    if (names().includes(name)) return language.t("project.remote.duplicateName")
  }
  const finish = (item: ServerConnection.Any, name: string) => {
    if (disposed) return
    const directory = `${root()}/${name}`
    dialog.close()
    props.onAdded?.(item, directory)
  }
  const add = () => {
    const item = ready()
    if (!item || !state.selected || added(state.selected) || !names().includes(state.selected)) return
    finish(item, state.selected)
  }
  const create = async () => {
    const item = ready()
    const name = projectName(state.name)
    if (!item || !name || nameError() || state.submitting) return
    setState({ submitting: true, error: "" })
    try {
      const sdk = global.ensureServerCtx(item).sdk
      await sdk.api.file.prepare(
        { kind: "directory", path: name, location: { directory: connection()!.directory } },
        { signal: abort.signal },
      )
      finish(item, name)
    } catch {
      setState("error", language.t("project.remote.createFailed"))
    } finally {
      setState("submitting", false)
    }
  }
  const creating = () => !names().length || state.creating
  const connectionError = () => {
    const item = server()
    return connection.error || (item && global.ensureServerCtx(item).sdk.connection.error())
  }
  const loading = () => !connectionError() && (connection.loading || !ready() || projects.loading)
  const loadError = () => !!connectionError() || !!projects.error
  const loaded = () => !!ready() && !loading() && !loadError()
  const retry = () => {
    if (state.submitting) return
    if (!ready() || connectionError()) {
      void reconnect()
      return
    }
    void refetch()
  }
  return (
    <Dialog fit class="remote-project-dialog" containerClass="remote-project-dialog-container">
      <DialogHeader hideClose={state.submitting}>
        <DialogTitle>{language.t("project.remote.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <Show when={loading()}>
          <div
            role="status"
            aria-label={language.t("project.remote.loading")}
            aria-busy="true"
            class="remote-project-loading"
          >
            <div aria-hidden="true" class="remote-project-skeleton">
              <div class="remote-project-skeleton-label" />
              <div class="remote-project-skeleton-input" />
            </div>
          </div>
        </Show>
        <Show when={loadError()}>
          <div class="remote-project-load-error" role="alert">
            <Icon name="folder-remote" size="large" />
            <p>{language.t("project.remote.loadFailed")}</p>
          </div>
        </Show>
        <Show when={loaded()}>
          <Show when={!creating()}>
            <TextInput
              class="remote-project-search"
              appearance="large"
              leadingIcon={<Icon name="magnifying-glass" size="small" />}
              placeholder={language.t("project.remote.search")}
              aria-label={language.t("project.remote.search")}
              value={state.search}
              autocomplete="off"
              ref={(element) => queueMicrotask(() => element.isConnected && element.focus())}
              showClearButton={!!state.search}
              onClearClick={() => setState("search", "")}
              onInput={(event) => {
                const search = event.currentTarget.value
                setState({
                  search,
                  selected: state.selected.toLowerCase().includes(search.trim().toLowerCase()) ? state.selected : "",
                })
              }}
            />
            <div class="remote-project-list" role="group" aria-label={language.t("project.remote.choose")}>
              <For
                each={filtered()}
                fallback={
                  <div class="remote-project-empty" role="status">
                    <strong>{language.t("project.remote.noMatches")}</strong>
                    <p>{language.t("project.remote.noMatchesDescription")}</p>
                  </div>
                }
              >
                {(name) => (
                  <button
                    type="button"
                    class="remote-project-option"
                    disabled={added(name)}
                    data-selected={state.selected === name || undefined}
                    aria-pressed={state.selected === name}
                    onClick={() => setState("selected", name)}
                  >
                    <Icon name={state.selected === name ? "folder-remote-open" : "folder-remote"} size="small" />
                    <span class="remote-project-option-name" title={name}>
                      {name}
                    </span>
                    <Show
                      when={added(name)}
                      fallback={
                        <Show when={state.selected === name}>
                          <Icon name="check" size="small" />
                        </Show>
                      }
                    >
                      <span class="remote-project-added">{language.t("project.remote.added")}</span>
                    </Show>
                  </button>
                )}
              </For>
            </div>
            <Button
              class="remote-project-new"
              variant="ghost"
              icon="plus"
              onClick={() => setState({ creating: true, selected: "", error: "" })}
            >
              {language.t("project.remote.new")}
            </Button>
          </Show>
          <Show when={creating()}>
            <Show when={names().length > 0}>
              <Button
                class="remote-project-back"
                variant="ghost"
                icon="arrow-left"
                disabled={state.submitting}
                onClick={() => setState({ creating: false, error: "" })}
              >
                {language.t("project.remote.back")}
              </Button>
            </Show>
            <label class="remote-project-label" for="remote-project-name">
              {language.t("project.remote.name")}
            </label>
            <TextInput
              id="remote-project-name"
              appearance="large"
              value={state.name}
              ref={(element) => queueMicrotask(() => element.isConnected && element.focus())}
              aria-describedby="remote-project-name-error"
              autocomplete="off"
              disabled={state.submitting}
              invalid={!!nameError() || !!state.error}
              onInput={(event) => setState({ name: event.currentTarget.value, error: "" })}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.isComposing) return
                event.preventDefault()
                void create()
              }}
            />
            <div id="remote-project-name-error">
              <Show when={nameError() || state.error}>
                {(error) => (
                  <p role="alert" class="remote-project-field-error">
                    {error()}
                  </p>
                )}
              </Show>
            </div>
          </Show>
        </Show>
      </DialogBody>
      <DialogFooter>
        <Button variant="neutral" disabled={state.submitting} onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </Button>
        <Show when={!loading()} fallback={<div class="remote-project-skeleton-action" aria-hidden="true" />}>
          <Show
            when={loadError()}
            fallback={
              <Button
                class="remote-project-action"
                variant="contrast"
                disabled={
                  !loaded() ||
                  state.submitting ||
                  (creating() ? !projectName(state.name) || !!nameError() : !state.selected || added(state.selected))
                }
                aria-busy={state.submitting}
                onClick={() => (creating() ? void create() : add())}
              >
                <Show when={state.submitting}>
                  <Spinner class="size-4" />
                </Show>
                {language.t(
                  state.submitting
                    ? "project.remote.creating"
                    : loaded() && creating()
                      ? "project.remote.create"
                      : "home.project.add",
                )}
              </Button>
            }
          >
            <Button class="remote-project-action" variant="contrast" disabled={state.submitting} onClick={retry}>
              {language.t("sair.retry")}
            </Button>
          </Show>
        </Show>
      </DialogFooter>
    </Dialog>
  )
}
